// The editable slides-markdown pane: a lazy-loaded CodeMirror editor (plain mode)
// with debounced autosave (PATCH). Forwards an imperative editor handle up to the
// page so AI rewrites can flush the latest text and reseed after apply. Mirrors
// the report's `latex-editor-panel.tsx`, minus the LaTeX compile pieces.
import { Suspense, lazy, useEffect, useRef, useState, type Ref } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "@/components/ui";
import type { CodeMirrorHandle } from "../report/codemirror-editor";

const CodeMirrorEditor = lazy(() => import("../report/codemirror-editor"));

const SAVE_DEBOUNCE_MS = 1000;

interface Props {
  documentKey: string;
  initialValue: string;
  onSave: (value: string) => Promise<unknown>;
  editorRef?: Ref<CodeMirrorHandle>;
}

export function SlideEditorPanel({ documentKey, initialValue, onSave, editorRef }: Props) {
  const { t } = useTranslation();
  const draft = useRef(initialValue);
  const dirty = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");

  useEffect(() => {
    draft.current = initialValue;
    dirty.current = false;
    setSaveState("idle");
  }, [documentKey, initialValue]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const flush = async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (!dirty.current) return;
    setSaveState("saving");
    try {
      await onSave(draft.current);
      dirty.current = false;
      setSaveState("saved");
    } catch {
      // Leave dirty so the next edit retries; surface errors via the page.
      setSaveState("idle");
    }
  };

  const handleChange = (value: string) => {
    draft.current = value;
    dirty.current = true;
    setSaveState("idle");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void flush(), SAVE_DEBOUNCE_MS);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-black/5 px-3 py-2">
        <span className="text-xs text-ink-faint">
          {saveState === "saving"
            ? t("Saving…")
            : saveState === "saved"
              ? t("Saved")
              : t("Slides (Markdown)")}
        </span>
      </div>
      <div className="min-h-0 flex-1">
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center">
              <Spinner />
            </div>
          }
        >
          <CodeMirrorEditor
            key={documentKey}
            ref={editorRef}
            initialValue={initialValue}
            onChange={handleChange}
            language="plain"
          />
        </Suspense>
      </div>
    </div>
  );
}
